import React, { useState } from 'react';
import Helmet from '../components/Helmet/Helmet';
import CommonSection from '../components/Ui/CommonSection';
import { Container, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import '../styles/contact.css';

const showrooms = [
  {
    city: 'Cairo Boutique',
    region: 'Zamalek Atelier',
    address: '22 El-Gezira Street, Zamalek, Cairo, Egypt',
    phone: '+20 (2) 2123-4567',
    hours: 'Sun – Thu: 10:00 AM – 9:00 PM &bull; Fri – Sat: 1:00 PM – 10:00 PM',
    type: 'Flagship Showroom & Material Lab',
  },
  {
    city: 'Paris Flagship',
    region: '1er Arrondissement',
    address: '14 Place Vendôme, 75001 Paris, France',
    phone: '+33 1 42 68 55 00',
    hours: 'Mon – Sat: 10:30 AM – 7:30 PM &bull; Closed Sundays',
    type: 'Heritage Studio & Private Salon',
  },
  {
    city: 'Milan Studio',
    region: 'Quadrilatero della Moda',
    address: 'Via Montenapoleone 8, 20121 Milano, Italy',
    phone: '+39 02 7600 4411',
    hours: 'Mon – Sat: 10:00 AM – 8:00 PM',
    type: 'Architectural Curation & Materials Lab',
  },
  {
    city: 'Dubai Salon',
    region: 'Dubai Design District',
    address: 'Building 4, Ground Floor, d3, Dubai, UAE',
    phone: '+971 4 455 8899',
    hours: 'Daily: 10:00 AM – 10:00 PM',
    type: 'Luxury Penthouse Curation Suite',
  },
];

const faqs = [
  {
    q: 'What does your White-Glove In-Home Delivery include?',
    a: 'Our White-Glove service includes scheduled appointment delivery by two uniformed specialists who carry the piece into your designated room, unbox, assemble, and position it to your satisfaction, followed by complete removal and recycling of all packing materials.',
  },
  {
    q: 'Can I request custom dimensions or specific leather/fabric colors?',
    a: 'Absolutely. As an authentic atelier, our craftsmen frequently build made-to-measure pieces. You can alter sofa lengths, specify bespoke wood finishes (walnut, ash, smoked oak), and choose from over 80 European textiles and Tuscan leathers.',
  },
  {
    q: 'What guarantees and trial periods do you offer?',
    a: 'Every handcrafted structural frame carries a 10-year atelier warranty. We also offer a 30-day in-home trial period for all catalog collection pieces to ensure complete harmony within your residence.',
  },
  {
    q: 'Do you deliver across Egypt and internationally?',
    a: 'Yes. We offer direct express white-glove transport across Greater Cairo, Alexandria, Giza, and coastal residences, as well as insured priority international freight to the GCC and Europe.',
  },
];

const Contact = () => {
  const [activeFaq, setActiveFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSending, setIsSending] = useState(false);

  const toggleFaq = (idx) => {
    setActiveFaq(activeFaq === idx ? null : idx);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error('Please fill in all required fields.');
      return;
    }

    setIsSending(true);
    setTimeout(() => {
      setIsSending(false);
      toast.success(
        `Thank you, ${formData.name}! Your message has been routed to our Senior Concierge desk.`
      );
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 700);
  };

  return (
    <Helmet title="Global Showrooms & Concierge | L'HABILLEUR">
      <CommonSection title="Showrooms & Concierge" />

      {/* Hero Section */}
      <section className="contact__hero-section">
        <Container>
          <span className="about__section-badge">Atelier Presence</span>
          <h2 className="fw-bold mb-3" style={{ color: 'var(--primary-color)', fontSize: '2.4rem' }}>
            Visit Our Salons or Inquire Directly
          </h2>
          <p className="text-muted mx-auto mb-0" style={{ maxWidth: '650px', fontSize: '1.05rem', lineHeight: '1.7' }}>
            Experience our artisanal furniture in person at our flagship salons in Cairo, Paris, Milan, and Dubai, or connect with our private concierge team.
          </p>
        </Container>
      </section>

      {/* Global Flagship Locations */}
      <section className="py-5 bg-white">
        <Container className="py-4">
          <div className="text-center mb-5">
            <span className="about__section-badge">Flagship Ateliers</span>
            <h3 className="fw-bold" style={{ color: 'var(--primary-color)' }}>
              Our International Destinations
            </h3>
          </div>

          <Row className="g-4">
            {showrooms.map((room, idx) => (
              <Col key={idx} lg="6" md="6">
                <div className="contact__showroom-card">
                  <span className="contact__city-badge">{room.region}</span>
                  <h4 className="contact__city-title">{room.city}</h4>
                  
                  <div className="contact__info-row">
                    <i className="ri-map-pin-2-line"></i>
                    <span>{room.address}</span>
                  </div>

                  <div className="contact__info-row">
                    <i className="ri-phone-line"></i>
                    <span>{room.phone}</span>
                  </div>

                  <div className="contact__info-row">
                    <i className="ri-time-line"></i>
                    <span dangerouslySetInnerHTML={{ __html: room.hours }}></span>
                  </div>

                  <div className="mt-3 pt-3 border-top d-flex justify-content-between align-items-center">
                    <span className="small text-muted fw-semibold">{room.type}</span>
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(room.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="small text-primary fw-bold text-decoration-none"
                    >
                      Get Directions &rarr;
                    </a>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Concierge Message Form & Direct Hotline */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container className="py-4">
          <Row className="g-5 align-items-center">
            <Col lg="5">
              <div className="pe-lg-4">
                <span className="about__section-badge">Direct Assistance</span>
                <h3 className="fw-bold mb-3" style={{ color: 'var(--primary-color)', fontSize: '2rem' }}>
                  Private Concierge & Customer Care
                </h3>
                <p className="text-muted mb-4">
                  Whether inquiring about a bespoke residential commission, delivery schedules, or product specifications, our concierge team is at your immediate service.
                </p>

                <div className="d-flex flex-column gap-3 mb-4">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '10px',
                        background: 'var(--accent-gold-soft)',
                        color: 'var(--accent-gold)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.3rem',
                      }}
                    >
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <small className="text-muted d-block">Concierge Hotline</small>
                      <strong>+20 (2) 2123-4567</strong>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '10px',
                        background: '#e0f2fe',
                        color: '#0284c7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.3rem',
                      }}
                    >
                      <i className="ri-mail-send-line"></i>
                    </div>
                    <div>
                      <small className="text-muted d-block">Direct Inquiries</small>
                      <strong>concierge@lhabilleur.com</strong>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '10px',
                        background: '#dcfce7',
                        color: '#16a34a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.3rem',
                      }}
                    >
                      <i className="ri-whatsapp-line"></i>
                    </div>
                    <div>
                      <small className="text-muted d-block">WhatsApp VIP Desk</small>
                      <strong>+20 10 2123 4567</strong>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            <Col lg="7">
              <div className="contact__card">
                <h4 className="fw-bold mb-4" style={{ color: 'var(--primary-color)' }}>
                  Send a Note to the Atelier
                </h4>

                <form onSubmit={handleSubmit}>
                  <Row className="g-3">
                    <Col md="6">
                      <div className="services__form-group">
                        <label>Your Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="e.g. Alexandre Dumas"
                          required
                        />
                      </div>
                    </Col>

                    <Col md="6">
                      <div className="services__form-group">
                        <label>Your Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="you@domain.com"
                          required
                        />
                      </div>
                    </Col>

                    <Col md="12">
                      <div className="services__form-group">
                        <label>Subject of Inquiry</label>
                        <input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="Custom size request, shipping question, or appointment..."
                        />
                      </div>
                    </Col>

                    <Col md="12">
                      <div className="services__form-group">
                        <label>Message *</label>
                        <textarea
                          rows="4"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Write your inquiry here..."
                          required
                        ></textarea>
                      </div>
                    </Col>

                    <Col md="12">
                      <button
                        type="submit"
                        className="buy__btn w-100 py-3"
                        disabled={isSending}
                      >
                        {isSending ? (
                          <span>
                            <i className="ri-loader-4-line ri-spin me-2"></i> Transmitting...
                          </span>
                        ) : (
                          <span>
                            <i className="ri-send-plane-fill me-2"></i> Send Inquiry to Concierge
                          </span>
                        )}
                      </button>
                    </Col>
                  </Row>
                </form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Frequently Answered Inquiries (FAQ) */}
      <section className="py-5 bg-white">
        <Container className="py-4">
          <div className="text-center mb-5">
            <span className="about__section-badge">Client Knowledge Base</span>
            <h3 className="fw-bold" style={{ color: 'var(--primary-color)' }}>
              Frequently Answered Inquiries
            </h3>
          </div>

          <Row className="justify-content-center">
            <Col lg="9">
              {faqs.map((faq, idx) => (
                <div
                  key={idx}
                  className={`contact__faq-item ${activeFaq === idx ? 'active' : ''}`}
                >
                  <div
                    className="contact__faq-header"
                    onClick={() => toggleFaq(idx)}
                  >
                    <span>{faq.q}</span>
                    <i className="ri-arrow-down-s-line"></i>
                  </div>
                  {activeFaq === idx && (
                    <div className="contact__faq-body">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </Col>
          </Row>
        </Container>
      </section>
    </Helmet>
  );
};

export default Contact;
